<template>
    <div class="status-ring" ref="ringRef">
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
            <!-- ✅ 그라디언트 + 필터 정의 -->
            <defs>
                <linearGradient
                    id="ring-gradient"
                    x1="0%"
                    y1="0%"
                    x2="140%"
                    y2="0%"
                    gradientTransform="rotate(20 0.5 0.5)"
                >
                    <stop ref="stop1" offset="0%" stop-color="#1377C4" />
                    <stop ref="stop2" offset="80%" stop-color="#00B2FF" />
                    <stop ref="stop3" offset="100%" stop-color="#FFFFFF" />
                </linearGradient>

                <!-- 흐르는 arc용 흰색 그라디언트 -->
                <!-- <linearGradient id="end-highlight" x1="0" y1="0" x2="1" y2="0">
                    <stop
                        offset="0%"
                        stop-color="#fff"
                        :stop-opacity="endHighlightStopOpacities[0]"
                    />
                    <stop
                        offset="100%"
                        stop-color="#fff"
                        :stop-opacity="endHighlightStopOpacities[1]"
                    />
                </linearGradient> -->

                <!-- ✅ 여기 추가: Blur 필터 정의 -->
                <defs>
                    <filter id="blur-filter" x="-20" y="-20" width="140" height="140" filterUnits="userSpaceOnUse">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
                    </filter>
                </defs>
            </defs>

            <!-- Background Circle -->
            <circle class="ring-bg" :cx="50" :cy="50" :r="radius" :stroke-width="strokeWidth" />

            <!-- Progress Circle -->
            <circle
                class="ring-progress"
                :cx="50"
                :cy="50"
                :r="radius"
                :stroke-width="strokeWidth"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
                stroke="url(#ring-gradient)"
            />

            <!-- 하이라이트 arc -->
            <circle
                class="ring-highlight"
                :cx="50"
                :cy="50"
                :r="radius"
                :stroke-width="strokeWidth"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
                stroke="url(#end-highlight)"
                stroke-linecap="round"
                fill="none"
            />

            <!-- ✅ 수정: End Dot에 SVG 필터 적용 -->
            <circle
                v-if="animatedValue > 0"
                class="ring-end-dot"
                :cx="progressDot.x"
                :cy="progressDot.y"
                :r="props.strokeWidth / 2"
                filter="url(#blur-filter)"
                fill="#fff"
            />

            <!-- Particles 컴포넌트 -->
            <transition name="fade">
                <Particles
                    v-if="!isAnimating"
                    :percentage="animatedValue"
                    :radius="radius"
                    :strokeWidth="strokeWidth"
                    :center="{ x: progressDot.x, y: progressDot.y }"
                />
            </transition>
        </svg>

        <!-- 퍼센트 텍스트 -->
        <div class="ring-text">
            <span class="ring-text-number h0">{{ Math.round(animatedValue) }}</span>
            <span class="ring-text-unit">%</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, nextTick } from 'vue';
import Particles from './Particles.vue';

interface Props {
    percentage: number;
    strokeWidth: number;
}
const props = defineProps<Props>();

const radius = 50 - props.strokeWidth / 2;
const circumference = 2 * Math.PI * radius;

const animatedValue = ref(0);
const dashOffset = ref(circumference);

const ringRef = ref<HTMLElement | null>(null);
const particlePos = ref<{ x: number; y: number } | null>(null);

const stop1 = ref<SVGStopElement | null>(null);
const stop2 = ref<SVGStopElement | null>(null);
const stop3 = ref<SVGStopElement | null>(null);

const endHighlightStopOpacities = ref([1, 0]);

const setEndHighlightStopOpacities = () => {
    if (props.percentage >= 90) {
        endHighlightStopOpacities.value = [0.2, 0];
    } else if (props.percentage >= 80) {
        endHighlightStopOpacities.value = [0, 0];
    } else if (props.percentage >= 70) {
        endHighlightStopOpacities.value = [0.8, 0];
    } else if (props.percentage >= 60) {
        endHighlightStopOpacities.value = [1, 0];
    } else if (props.percentage >= 50) {
        endHighlightStopOpacities.value = [1, 0];
    } else {
        endHighlightStopOpacities.value = [1, 0];
    }
};

const isAnimating = ref(true);

const animate = () => {
    isAnimating.value = true;
    const duration = 5000;
    const start = performance.now();

    const step = (timestamp: number) => {
        const progress = Math.min((timestamp - start) / duration, 1);
        animatedValue.value = props.percentage * easeOutCubic(progress);
        dashOffset.value = circumference * (1 - animatedValue.value / 100);

        updateParticlePosition();

        if (progress < 1) {
            requestAnimationFrame(step);
        } else {
            isAnimating.value = false; // ✅ 애니메이션 끝난 후에 false 처리
        }
    };

    requestAnimationFrame(step);
};

//다양한 주기의 sin을 섞어서 "연속적인 랜덤 느낌"을 만드는 방식
function perlinNoise1D(t: number): number {
    // 기본적인 1D perlin-like 함수 (간단 구현)
    return Math.sin(t * 0.8) * 0.5 + Math.sin(t * 1.7 + 10) * 0.25 + Math.sin(t * 2.9 + 25) * 0.25;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const updateParticlePosition = () => {
    if (!ringRef.value) return;

    const rect = ringRef.value.getBoundingClientRect();
    const angle = (animatedValue.value / 100) * 2 * Math.PI; // ⛔️ -PI/2 제거 (회전 보정 X)
    const radiusPx = rect.width / 2 - props.strokeWidth / 2;

    const x = rect.width / 2 + Math.cos(angle) * radiusPx;
    const y = rect.height / 2 + Math.sin(angle) * radiusPx;

    particlePos.value = { x, y };
};

const progressDot = computed(() => {
    const angle = (animatedValue.value / 100) * 2 * Math.PI;
    const r = radius;

    const x = 50 + Math.cos(angle) * r;
    const y = 50 + Math.sin(angle) * r;

    return { x, y };
});

const startGradientAnimation = () => {
    const start = performance.now();
    const animateGradient = (time: number) => {
        const t = (time - start) / 500;
        const base1 = perlinNoise1D(t * 0.5) * 20;
        const base2 = perlinNoise1D(t * 0.7 + 10) * 15;
        const base3 = perlinNoise1D(t * 0.3 + 30) * 25;

        if (stop1.value) stop1.value.setAttribute('offset', `${clamp(10 + base1, 0, 80)}%`);
        if (stop2.value) stop2.value.setAttribute('offset', `${clamp(55 + base2, 0, 100)}%`);
        if (stop3.value) stop3.value.setAttribute('offset', `${clamp(90 + base3, 0, 120)}%`);

        requestAnimationFrame(animateGradient);
    };
    requestAnimationFrame(animateGradient);
};

onMounted(() => {
    nextTick(() => {
        requestAnimationFrame(() => {
            setEndHighlightStopOpacities();
            animate();
            startGradientAnimation();
        });
    });
});

watch(
    () => props.percentage,
    () => {
        animate();
    },
);
</script>

<style scoped lang="scss">
.status-ring {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: visible;
}

svg {
    width: 100%;
    height: 100%;
    display: block;
    transform: rotate(-90deg);
    overflow: visible;
}

.ring-bg {
    fill: none;
    stroke: $grey-9;
}

.ring-progress {
    fill: none;
    stroke-linecap: round;
}

.ring-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
}

.ring-text-unit {
    font-size: 2.3rem;
    font-weight: 400;
    line-height: 100%;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s ease-in;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
